import type {
  SpecialistAvailability,
  SpecialistAvailabilityStatus,
  SpecialistCertificate,
  SpecialistDesiredWorkload,
  SpecialistEducation,
  SpecialistEmploymentType,
  SpecialistLanguage,
  SpecialistLanguageProficiency,
  SpecialistPortfolioProject,
  SpecialistProfile,
  SpecialistProfileSkill,
  SpecialistRateModel,
  SpecialistWorkExperience,
  SpecialistWorkFormat,
  SpecialistWorkPreferences,
} from "@brainics/api-contract";
import { useEffect, useState, type FormEvent } from "react";

import { useAuth } from "../../auth/AuthProvider.js";
import { ApiClientError } from "../../lib/api.js";
import {
  createSpecialistCertificate,
  createSpecialistEducation,
  createSpecialistLanguage,
  createSpecialistPortfolioProject,
  createSpecialistProfile,
  createSpecialistSkill,
  createSpecialistWorkExperience,
  getSpecialistAvailability,
  getSpecialistProfile,
  getSpecialistWorkPreferences,
  listSpecialistCertificates,
  listSpecialistEducations,
  listSpecialistLanguages,
  listSpecialistPortfolioProjects,
  listSpecialistSkills,
  listSpecialistWorkExperiences,
  putSpecialistAvailability,
  putSpecialistWorkPreferences,
  updateSpecialistProfile,
} from "../../lib/specialistApi.js";

const specialistIdStorageKey = "brainics.currentSpecialistId";
const companyIdStorageKey = "brainics.currentCompanyId";

function errorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.code === "SPECIALIST_PROFILE_NOT_FOUND") return "Профиль специалиста не найден.";
    if (error.code === "AUTHENTICATION_REQUIRED") return "Нужно войти в аккаунт.";
    if (error.status === 403) return "Недостаточно прав.";
    return error.message;
  }

  return "Не удалось выполнить запрос.";
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function listFromText(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function useStoredId(storageKey: string) {
  const [value, setValueState] = useState(() => {
    try {
      return localStorage.getItem(storageKey) ?? "";
    } catch {
      return "";
    }
  });

  function setValue(next: string) {
    const trimmed = next.trim();
    setValueState(trimmed);
    try {
      if (trimmed) localStorage.setItem(storageKey, trimmed);
      else localStorage.removeItem(storageKey);
    } catch {
      // ignore localStorage restrictions
    }
  }

  return { value, setValue };
}

function PageShell({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-6xl">
      <p className="text-sm font-semibold text-cyan-300">{eyebrow}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
      <p className="mt-3 max-w-3xl leading-7 text-slate-400">{description}</p>
      <div className="mt-8">{children}</div>
    </div>
  );
}

function InfoPanel({
  title,
  text,
  tone = "default",
}: {
  title: string;
  text: string;
  tone?: "default" | "danger" | "success";
}) {
  const className =
    tone === "danger"
      ? "border-rose-300/15 bg-rose-300/[0.06] text-rose-100"
      : tone === "success"
        ? "border-emerald-300/15 bg-emerald-300/[0.06] text-emerald-100"
        : "border-white/10 bg-white/[0.035] text-slate-300";

  return (
    <div className={`rounded-2xl border p-5 ${className}`}>
      <p className="font-semibold">{title}</p>
      <p className="mt-2 text-sm leading-6 opacity-80">{text}</p>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm text-slate-400">{label}</span>
      <input
        className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={type}
        value={value}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm text-slate-400">{label}</span>
      <textarea
        className="mt-2 min-h-28 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        value={value}
      />
    </label>
  );
}

function IdPanel() {
  const specialist = useStoredId(specialistIdStorageKey);
  const company = useStoredId(companyIdStorageKey);
  const [specialistDraft, setSpecialistDraft] = useState(specialist.value);
  const [companyDraft, setCompanyDraft] = useState(company.value);

  useEffect(() => setSpecialistDraft(specialist.value), [specialist.value]);
  useEffect(() => setCompanyDraft(company.value), [company.value]);

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        Текущий специалист
      </p>
      <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_auto]">
        <input
          className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
          onChange={(event) => setSpecialistDraft(event.target.value)}
          placeholder="specialistId UUID"
          value={specialistDraft}
        />
        <button
          className="rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200"
          onClick={() => specialist.setValue(specialistDraft)}
          type="button"
        >
          Сохранить specialistId
        </button>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_auto]">
        <input
          className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
          onChange={(event) => setCompanyDraft(event.target.value)}
          placeholder="vendorCompanyId UUID, если профиль принадлежит подрядчику"
          value={companyDraft}
        />
        <button
          className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 transition hover:bg-white/5"
          onClick={() => company.setValue(companyDraft)}
          type="button"
        >
          Сохранить companyId
        </button>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-500">
        В этой версии frontend использует specialistId из localStorage или ручной ввод. Для
        vendor-owned профиля используется companyId из кабинета компании.
      </p>
    </section>
  );
}

function useSpecialistId() {
  return useStoredId(specialistIdStorageKey);
}

function SpecialistProfileCard({ profile }: { profile: SpecialistProfile }) {
  return (
    <section className="grid gap-4 md:grid-cols-4">
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Тип</p>
        <p className="mt-2 text-lg font-semibold">{profile.ownershipType}</p>
      </article>
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Статус</p>
        <p className="mt-2 text-lg font-semibold">{profile.status}</p>
      </article>
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Опыт</p>
        <p className="mt-2 text-lg font-semibold">{profile.yearsOfExperience ?? "—"}</p>
      </article>
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Обновлено</p>
        <p className="mt-2 text-lg font-semibold">{formatDateTime(profile.updatedAt)}</p>
      </article>
    </section>
  );
}

export function SpecialistProfilePage() {
  const auth = useAuth();
  const specialistId = useSpecialistId();
  const companyId = useStoredId(companyIdStorageKey);
  const [profile, setProfile] = useState<SpecialistProfile | null>(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    countryCode: "",
    city: "",
    timezone: "",
    primarySpecialization: "",
    grade: "",
    yearsOfExperience: "",
    summary: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState<"independent" | "vendor_owned" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function fillForm(next: SpecialistProfile) {
    setForm({
      firstName: next.firstName ?? "",
      lastName: next.lastName ?? "",
      countryCode: next.countryCode ?? "",
      city: next.city ?? "",
      timezone: next.timezone ?? "",
      primarySpecialization: next.primarySpecialization ?? "",
      grade: next.grade ?? "",
      yearsOfExperience: next.yearsOfExperience === null ? "" : String(next.yearsOfExperience),
      summary: next.summary ?? "",
    });
  }

  async function load() {
    if (!specialistId.value) return;
    setLoading(true);
    setError(null);

    try {
      const next = await getSpecialistProfile(specialistId.value);
      setProfile(next);
      fillForm(next);
    } catch (requestError) {
      setProfile(null);
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function createProfile(kind: "independent" | "vendor_owned") {
    setCreating(kind);
    setError(null);
    setNotice(null);

    try {
      const next =
        kind === "independent"
          ? await createSpecialistProfile({ ownershipType: "independent" })
          : await createSpecialistProfile({
              ownershipType: "vendor_owned",
              vendorCompanyId: companyId.value,
            });

      specialistId.setValue(next.id);
      setProfile(next);
      fillForm(next);
      setNotice("Профиль специалиста создан.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setCreating(null);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!specialistId.value) return;
    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const next = await updateSpecialistProfile(specialistId.value, {
        firstName: nullable(form.firstName),
        lastName: nullable(form.lastName),
        countryCode: nullable(form.countryCode),
        city: nullable(form.city),
        timezone: nullable(form.timezone),
        primarySpecialization: nullable(form.primarySpecialization),
        grade: nullable(form.grade),
        yearsOfExperience: numberOrNull(form.yearsOfExperience),
        summary: nullable(form.summary),
      });
      setProfile(next);
      fillForm(next);
      setNotice("Профиль сохранён.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  const canCreateIndependent = auth.user?.registrationType === "independent_specialist";
  const canCreateVendor =
    auth.user?.registrationType === "vendor_owner" ||
    auth.user?.registrationType === "platform_staff";

  return (
    <PageShell
      eyebrow="Специалист"
      title="Профиль специалиста"
      description="Создание, загрузка и редактирование основной карточки специалиста."
    >
      <div className="space-y-6">
        <IdPanel />

        <section className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
            Создать профиль
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className="rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
              disabled={!canCreateIndependent || creating !== null}
              onClick={() => void createProfile("independent")}
              type="button"
            >
              {creating === "independent" ? "Создаём…" : "Создать independent"}
            </button>
            <button
              className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 transition hover:bg-white/5 disabled:opacity-60"
              disabled={!canCreateVendor || !companyId.value || creating !== null}
              onClick={() => void createProfile("vendor_owned")}
              type="button"
            >
              {creating === "vendor_owned" ? "Создаём…" : "Создать vendor-owned"}
            </button>
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Independent создаётся для аккаунта специалиста. Vendor-owned требует companyId.
          </p>
        </section>

        {loading ? <InfoPanel title="Загрузка" text="Получаем профиль специалиста…" /> : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        {notice ? <InfoPanel title="Готово" text={notice} tone="success" /> : null}

        {profile ? <SpecialistProfileCard profile={profile} /> : null}

        {specialistId.value ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void save(event)}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Основные данные
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <TextInput
                label="Имя"
                onChange={(value) => setForm((current) => ({ ...current, firstName: value }))}
                value={form.firstName}
              />
              <TextInput
                label="Фамилия"
                onChange={(value) => setForm((current) => ({ ...current, lastName: value }))}
                value={form.lastName}
              />
              <TextInput
                label="Страна ISO-2"
                onChange={(value) =>
                  setForm((current) => ({ ...current, countryCode: value.toUpperCase() }))
                }
                placeholder="RU"
                value={form.countryCode}
              />
              <TextInput
                label="Город"
                onChange={(value) => setForm((current) => ({ ...current, city: value }))}
                value={form.city}
              />
              <TextInput
                label="Timezone"
                onChange={(value) => setForm((current) => ({ ...current, timezone: value }))}
                placeholder="Europe/Moscow"
                value={form.timezone}
              />
              <TextInput
                label="Опыт, лет"
                onChange={(value) =>
                  setForm((current) => ({ ...current, yearsOfExperience: value }))
                }
                type="number"
                value={form.yearsOfExperience}
              />
              <TextInput
                label="Специализация"
                onChange={(value) =>
                  setForm((current) => ({ ...current, primarySpecialization: value }))
                }
                value={form.primarySpecialization}
              />
              <TextInput
                label="Grade"
                onChange={(value) => setForm((current) => ({ ...current, grade: value }))}
                value={form.grade}
              />
              <div className="md:col-span-2">
                <TextArea
                  label="Summary"
                  onChange={(value) => setForm((current) => ({ ...current, summary: value }))}
                  value={form.summary}
                />
              </div>
            </div>
            <button
              className="mt-6 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
              disabled={saving}
              type="submit"
            >
              {saving ? "Сохраняем…" : "Сохранить профиль"}
            </button>
          </form>
        ) : null}
      </div>
    </PageShell>
  );
}

function SpecialistRequiredId({ specialistId }: { specialistId: string }) {
  if (specialistId) return null;
  return (
    <InfoPanel
      title="Укажите specialistId"
      text="Для работы с этим разделом нужен ID профиля специалиста."
    />
  );
}

export function SpecialistSkillsPage() {
  const specialistId = useSpecialistId();
  const [items, setItems] = useState<SpecialistProfileSkill[]>([]);
  const [form, setForm] = useState({
    skillId: "",
    level: "3",
    yearsOfExperience: "",
    lastUsedAt: "",
    isKeySkill: false,
  });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!specialistId.value) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listSpecialistSkills(specialistId.value);
      setItems(response.items);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!specialistId.value) return;
    setCreating(true);
    setError(null);
    try {
      const item = await createSpecialistSkill(specialistId.value, {
        skillId: form.skillId,
        level: Number(form.level),
        yearsOfExperience: numberOrNull(form.yearsOfExperience),
        lastUsedAt: nullable(form.lastUsedAt),
        isKeySkill: form.isKeySkill,
        source: "self_reported",
      });
      setItems((current) => [item, ...current]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setCreating(false);
    }
  }

  return (
    <PageShell
      eyebrow="Специалист"
      title="Навыки"
      description="Добавление навыков по skillId из справочника."
    >
      <div className="space-y-6">
        <IdPanel />
        <SpecialistRequiredId specialistId={specialistId.value} />
        {specialistId.value ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void create(event)}
          >
            <div className="grid gap-4 md:grid-cols-4">
              <TextInput
                label="Skill ID"
                onChange={(value) => setForm((current) => ({ ...current, skillId: value }))}
                value={form.skillId}
              />
              <TextInput
                label="Level 1-5"
                onChange={(value) => setForm((current) => ({ ...current, level: value }))}
                type="number"
                value={form.level}
              />
              <TextInput
                label="Опыт, лет"
                onChange={(value) =>
                  setForm((current) => ({ ...current, yearsOfExperience: value }))
                }
                type="number"
                value={form.yearsOfExperience}
              />
              <TextInput
                label="Last used YYYY-MM-DD"
                onChange={(value) => setForm((current) => ({ ...current, lastUsedAt: value }))}
                value={form.lastUsedAt}
              />
            </div>
            <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
              <input
                checked={form.isKeySkill}
                onChange={(event) =>
                  setForm((current) => ({ ...current, isKeySkill: event.target.checked }))
                }
                type="checkbox"
              />
              Ключевой навык
            </label>
            <button
              className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-60"
              disabled={creating || !form.skillId}
              type="submit"
            >
              {creating ? "Добавляем…" : "Добавить навык"}
            </button>
          </form>
        ) : null}
        {loading ? <InfoPanel title="Загрузка" text="Получаем навыки…" /> : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        <ListCard title="Навыки" empty="Навыки пока не добавлены.">
          {items.map((item) => (
            <CompactItem
              key={item.id}
              title={`Skill ${item.skillId}`}
              meta={`Level ${item.level} · ${item.yearsOfExperience ?? "—"} лет · ${item.verificationState}`}
            />
          ))}
        </ListCard>
      </div>
    </PageShell>
  );
}

function ListCard({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode[];
}) {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
      <h2 className="text-xl font-semibold">{title}</h2>
      <div className="mt-4 space-y-3">
        {children.length === 0 ? <p className="text-sm text-slate-500">{empty}</p> : children}
      </div>
    </section>
  );
}

function CompactItem({
  title,
  meta,
  text,
}: {
  title: string;
  meta?: string | undefined;
  text?: string | null;
}) {
  return (
    <article className="rounded-2xl border border-white/10 bg-slate-950 p-4">
      <p className="font-medium text-slate-100">{title}</p>
      {meta ? <p className="mt-1 text-sm text-slate-500">{meta}</p> : null}
      {text ? <p className="mt-3 text-sm leading-6 text-slate-400">{text}</p> : null}
    </article>
  );
}

export function SpecialistExperiencePage() {
  const specialistId = useSpecialistId();
  const [items, setItems] = useState<SpecialistWorkExperience[]>([]);
  const [form, setForm] = useState({
    companyName: "",
    roleTitle: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    industry: "",
    responsibilities: "",
    achievements: "",
    technologies: "",
  });
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!specialistId.value) return;
    try {
      const response = await listSpecialistWorkExperiences(specialistId.value);
      setItems(response.items);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!specialistId.value) return;
    setError(null);
    try {
      const item = await createSpecialistWorkExperience(specialistId.value, {
        companyName: form.companyName,
        roleTitle: form.roleTitle,
        startDate: form.startDate,
        endDate: nullable(form.endDate),
        isCurrent: form.isCurrent,
        industry: nullable(form.industry),
        responsibilities: nullable(form.responsibilities),
        achievements: nullable(form.achievements),
        technologies: listFromText(form.technologies),
        sortOrder: 0,
      });
      setItems((current) => [item, ...current]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <PageShell eyebrow="Специалист" title="Опыт" description="Рабочий опыт специалиста.">
      <div className="space-y-6">
        <IdPanel />
        <SpecialistRequiredId specialistId={specialistId.value} />
        {specialistId.value ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void create(event)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <TextInput
                label="Компания"
                onChange={(value) => setForm((current) => ({ ...current, companyName: value }))}
                value={form.companyName}
              />
              <TextInput
                label="Роль"
                onChange={(value) => setForm((current) => ({ ...current, roleTitle: value }))}
                value={form.roleTitle}
              />
              <TextInput
                label="Start YYYY-MM-DD"
                onChange={(value) => setForm((current) => ({ ...current, startDate: value }))}
                value={form.startDate}
              />
              <TextInput
                label="End YYYY-MM-DD"
                onChange={(value) => setForm((current) => ({ ...current, endDate: value }))}
                value={form.endDate}
              />
              <TextInput
                label="Отрасль"
                onChange={(value) => setForm((current) => ({ ...current, industry: value }))}
                value={form.industry}
              />
              <TextInput
                label="Технологии через запятую"
                onChange={(value) => setForm((current) => ({ ...current, technologies: value }))}
                value={form.technologies}
              />
              <div className="md:col-span-2">
                <TextArea
                  label="Обязанности"
                  onChange={(value) =>
                    setForm((current) => ({ ...current, responsibilities: value }))
                  }
                  value={form.responsibilities}
                />
              </div>
              <div className="md:col-span-2">
                <TextArea
                  label="Достижения"
                  onChange={(value) => setForm((current) => ({ ...current, achievements: value }))}
                  value={form.achievements}
                />
              </div>
            </div>
            <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
              <input
                checked={form.isCurrent}
                onChange={(event) =>
                  setForm((current) => ({ ...current, isCurrent: event.target.checked }))
                }
                type="checkbox"
              />
              Текущее место
            </label>
            <button
              className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
              type="submit"
            >
              Добавить опыт
            </button>
          </form>
        ) : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        <ListCard title="Опыт" empty="Опыт пока не добавлен.">
          {items.map((item) => (
            <CompactItem
              key={item.id}
              title={`${item.roleTitle} · ${item.companyName}`}
              meta={`${item.startDate} — ${item.endDate ?? "сейчас"}`}
              text={item.responsibilities}
            />
          ))}
        </ListCard>
      </div>
    </PageShell>
  );
}

export function SpecialistPortfolioPage() {
  const specialistId = useSpecialistId();
  const [items, setItems] = useState<SpecialistPortfolioProject[]>([]);
  const [form, setForm] = useState({
    projectName: "",
    projectRole: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    industry: "",
    tasks: "",
    result: "",
    stack: "",
    links: "",
    isNda: false,
  });
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!specialistId.value) return;
    try {
      const response = await listSpecialistPortfolioProjects(specialistId.value);
      setItems(response.items);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!specialistId.value) return;
    setError(null);
    try {
      const item = await createSpecialistPortfolioProject(specialistId.value, {
        projectName: form.projectName,
        projectRole: form.projectRole,
        startDate: nullable(form.startDate),
        endDate: nullable(form.endDate),
        isCurrent: form.isCurrent,
        industry: nullable(form.industry),
        tasks: nullable(form.tasks),
        result: nullable(form.result),
        stack: listFromText(form.stack),
        links: listFromText(form.links),
        isNda: form.isNda,
        sortOrder: 0,
      });
      setItems((current) => [item, ...current]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <PageShell eyebrow="Специалист" title="Портфолио" description="Проекты и кейсы специалиста.">
      <div className="space-y-6">
        <IdPanel />
        <SpecialistRequiredId specialistId={specialistId.value} />
        {specialistId.value ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void create(event)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <TextInput
                label="Проект"
                onChange={(value) => setForm((current) => ({ ...current, projectName: value }))}
                value={form.projectName}
              />
              <TextInput
                label="Роль"
                onChange={(value) => setForm((current) => ({ ...current, projectRole: value }))}
                value={form.projectRole}
              />
              <TextInput
                label="Start YYYY-MM-DD"
                onChange={(value) => setForm((current) => ({ ...current, startDate: value }))}
                value={form.startDate}
              />
              <TextInput
                label="End YYYY-MM-DD"
                onChange={(value) => setForm((current) => ({ ...current, endDate: value }))}
                value={form.endDate}
              />
              <TextInput
                label="Отрасль"
                onChange={(value) => setForm((current) => ({ ...current, industry: value }))}
                value={form.industry}
              />
              <TextInput
                label="Stack через запятую"
                onChange={(value) => setForm((current) => ({ ...current, stack: value }))}
                value={form.stack}
              />
              <div className="md:col-span-2">
                <TextInput
                  label="Links через запятую"
                  onChange={(value) => setForm((current) => ({ ...current, links: value }))}
                  value={form.links}
                />
              </div>
              <div className="md:col-span-2">
                <TextArea
                  label="Задачи"
                  onChange={(value) => setForm((current) => ({ ...current, tasks: value }))}
                  value={form.tasks}
                />
              </div>
              <div className="md:col-span-2">
                <TextArea
                  label="Результат"
                  onChange={(value) => setForm((current) => ({ ...current, result: value }))}
                  value={form.result}
                />
              </div>
            </div>
            <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
              <input
                checked={form.isNda}
                onChange={(event) =>
                  setForm((current) => ({ ...current, isNda: event.target.checked }))
                }
                type="checkbox"
              />
              NDA
            </label>
            <button
              className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
              type="submit"
            >
              Добавить проект
            </button>
          </form>
        ) : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        <ListCard title="Портфолио" empty="Проекты пока не добавлены.">
          {items.map((item) => (
            <CompactItem
              key={item.id}
              title={`${item.projectName} · ${item.projectRole}`}
              meta={`${item.startDate ?? "—"} — ${item.endDate ?? "сейчас"}`}
              text={item.result}
            />
          ))}
        </ListCard>
      </div>
    </PageShell>
  );
}

export function SpecialistEducationPage() {
  const specialistId = useSpecialistId();
  const [educations, setEducations] = useState<SpecialistEducation[]>([]);
  const [certificates, setCertificates] = useState<SpecialistCertificate[]>([]);
  const [languages, setLanguages] = useState<SpecialistLanguage[]>([]);
  const [education, setEducation] = useState({
    institutionName: "",
    degree: "",
    fieldOfStudy: "",
    startDate: "",
    endDate: "",
    description: "",
  });
  const [certificate, setCertificate] = useState({
    certificateName: "",
    issuerName: "",
    issuedAt: "",
    expiresAt: "",
    credentialUrl: "",
  });
  const [language, setLanguage] = useState({
    languageCode: "en",
    languageName: "English",
    proficiency: "b2" as SpecialistLanguageProficiency,
    isPrimary: false,
  });
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!specialistId.value) return;
    try {
      const [educationResponse, certificateResponse, languageResponse] = await Promise.all([
        listSpecialistEducations(specialistId.value),
        listSpecialistCertificates(specialistId.value),
        listSpecialistLanguages(specialistId.value),
      ]);
      setEducations(educationResponse.items);
      setCertificates(certificateResponse.items);
      setLanguages(languageResponse.items);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function addEducation(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const item = await createSpecialistEducation(specialistId.value, {
        institutionName: education.institutionName,
        degree: nullable(education.degree),
        fieldOfStudy: nullable(education.fieldOfStudy),
        startDate: nullable(education.startDate),
        endDate: nullable(education.endDate),
        description: nullable(education.description),
        isCurrent: false,
        sortOrder: 0,
      });
      setEducations((current) => [item, ...current]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function addCertificate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const item = await createSpecialistCertificate(specialistId.value, {
        certificateName: certificate.certificateName,
        issuerName: certificate.issuerName,
        issuedAt: nullable(certificate.issuedAt),
        expiresAt: nullable(certificate.expiresAt),
        credentialUrl: nullable(certificate.credentialUrl),
        credentialId: null,
        description: null,
        sortOrder: 0,
      });
      setCertificates((current) => [item, ...current]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function addLanguage(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const item = await createSpecialistLanguage(specialistId.value, {
        languageCode: language.languageCode,
        languageName: language.languageName,
        proficiency: language.proficiency,
        isPrimary: language.isPrimary,
        certificateName: null,
        notes: null,
        sortOrder: 0,
      });
      setLanguages((current) => [item, ...current]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <PageShell
      eyebrow="Специалист"
      title="Образование"
      description="Образование, сертификаты и языки специалиста."
    >
      <div className="space-y-6">
        <IdPanel />
        <SpecialistRequiredId specialistId={specialistId.value} />
        {specialistId.value ? (
          <section className="grid gap-6 xl:grid-cols-3">
            <form
              className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
              onSubmit={(event) => void addEducation(event)}
            >
              <h2 className="text-xl font-semibold">Образование</h2>
              <div className="mt-4 space-y-4">
                <TextInput
                  label="Учебное заведение"
                  onChange={(value) =>
                    setEducation((current) => ({ ...current, institutionName: value }))
                  }
                  value={education.institutionName}
                />
                <TextInput
                  label="Степень"
                  onChange={(value) => setEducation((current) => ({ ...current, degree: value }))}
                  value={education.degree}
                />
                <TextInput
                  label="Направление"
                  onChange={(value) =>
                    setEducation((current) => ({ ...current, fieldOfStudy: value }))
                  }
                  value={education.fieldOfStudy}
                />
                <TextInput
                  label="Start YYYY-MM-DD"
                  onChange={(value) =>
                    setEducation((current) => ({ ...current, startDate: value }))
                  }
                  value={education.startDate}
                />
                <TextInput
                  label="End YYYY-MM-DD"
                  onChange={(value) => setEducation((current) => ({ ...current, endDate: value }))}
                  value={education.endDate}
                />
              </div>
              <button
                className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
                type="submit"
              >
                Добавить
              </button>
            </form>

            <form
              className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
              onSubmit={(event) => void addCertificate(event)}
            >
              <h2 className="text-xl font-semibold">Сертификат</h2>
              <div className="mt-4 space-y-4">
                <TextInput
                  label="Название"
                  onChange={(value) =>
                    setCertificate((current) => ({ ...current, certificateName: value }))
                  }
                  value={certificate.certificateName}
                />
                <TextInput
                  label="Issuer"
                  onChange={(value) =>
                    setCertificate((current) => ({ ...current, issuerName: value }))
                  }
                  value={certificate.issuerName}
                />
                <TextInput
                  label="Issued YYYY-MM-DD"
                  onChange={(value) =>
                    setCertificate((current) => ({ ...current, issuedAt: value }))
                  }
                  value={certificate.issuedAt}
                />
                <TextInput
                  label="Expires YYYY-MM-DD"
                  onChange={(value) =>
                    setCertificate((current) => ({ ...current, expiresAt: value }))
                  }
                  value={certificate.expiresAt}
                />
                <TextInput
                  label="Credential URL"
                  onChange={(value) =>
                    setCertificate((current) => ({ ...current, credentialUrl: value }))
                  }
                  value={certificate.credentialUrl}
                />
              </div>
              <button
                className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
                type="submit"
              >
                Добавить
              </button>
            </form>

            <form
              className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
              onSubmit={(event) => void addLanguage(event)}
            >
              <h2 className="text-xl font-semibold">Язык</h2>
              <div className="mt-4 space-y-4">
                <TextInput
                  label="Code"
                  onChange={(value) =>
                    setLanguage((current) => ({ ...current, languageCode: value }))
                  }
                  value={language.languageCode}
                />
                <TextInput
                  label="Название"
                  onChange={(value) =>
                    setLanguage((current) => ({ ...current, languageName: value }))
                  }
                  value={language.languageName}
                />
                <select
                  className="w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100"
                  onChange={(event) =>
                    setLanguage((current) => ({
                      ...current,
                      proficiency: event.target.value as SpecialistLanguageProficiency,
                    }))
                  }
                  value={language.proficiency}
                >
                  {["a1", "a2", "b1", "b2", "c1", "c2", "native"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-3 text-sm text-slate-300">
                  <input
                    checked={language.isPrimary}
                    onChange={(event) =>
                      setLanguage((current) => ({ ...current, isPrimary: event.target.checked }))
                    }
                    type="checkbox"
                  />
                  Основной язык
                </label>
              </div>
              <button
                className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
                type="submit"
              >
                Добавить
              </button>
            </form>
          </section>
        ) : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        <section className="grid gap-6 xl:grid-cols-3">
          <ListCard title="Образование" empty="Нет записей.">
            {educations.map((item) => (
              <CompactItem
                key={item.id}
                title={item.institutionName}
                meta={item.degree ?? undefined}
                text={item.description}
              />
            ))}
          </ListCard>
          <ListCard title="Сертификаты" empty="Нет сертификатов.">
            {certificates.map((item) => (
              <CompactItem
                key={item.id}
                title={item.certificateName}
                meta={item.issuerName}
                text={item.credentialUrl}
              />
            ))}
          </ListCard>
          <ListCard title="Языки" empty="Нет языков.">
            {languages.map((item) => (
              <CompactItem
                key={item.id}
                title={item.languageName}
                meta={`${item.languageCode} · ${item.proficiency}`}
              />
            ))}
          </ListCard>
        </section>
      </div>
    </PageShell>
  );
}

export function SpecialistRatesPage() {
  const specialistId = useSpecialistId();
  const [item, setItem] = useState<SpecialistWorkPreferences | null>(null);
  const [form, setForm] = useState({
    rateModel: "negotiable" as SpecialistRateModel,
    currencyCode: "RUB",
    hourlyRateMin: "",
    hourlyRateMax: "",
    monthlyRateMin: "",
    monthlyRateMax: "",
    workFormat: "flexible" as SpecialistWorkFormat,
    employmentType: "contract" as SpecialistEmploymentType,
    desiredWorkload: "flexible" as SpecialistDesiredWorkload,
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    if (!specialistId.value) return;
    try {
      const response = await getSpecialistWorkPreferences(specialistId.value);
      setItem(response.item);
      if (response.item) {
        setForm({
          rateModel: response.item.rateModel,
          currencyCode: response.item.currencyCode,
          hourlyRateMin:
            response.item.hourlyRateMin === null ? "" : String(response.item.hourlyRateMin),
          hourlyRateMax:
            response.item.hourlyRateMax === null ? "" : String(response.item.hourlyRateMax),
          monthlyRateMin:
            response.item.monthlyRateMin === null ? "" : String(response.item.monthlyRateMin),
          monthlyRateMax:
            response.item.monthlyRateMax === null ? "" : String(response.item.monthlyRateMax),
          workFormat: response.item.workFormat,
          employmentType: response.item.employmentType,
          desiredWorkload: response.item.desiredWorkload,
          notes: response.item.notes ?? "",
        });
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const next = await putSpecialistWorkPreferences(specialistId.value, {
        rateModel: form.rateModel,
        currencyCode: form.currencyCode,
        hourlyRateMin: numberOrNull(form.hourlyRateMin),
        hourlyRateMax: numberOrNull(form.hourlyRateMax),
        monthlyRateMin: numberOrNull(form.monthlyRateMin),
        monthlyRateMax: numberOrNull(form.monthlyRateMax),
        workFormat: form.workFormat,
        employmentType: form.employmentType,
        desiredWorkload: form.desiredWorkload,
        notes: nullable(form.notes),
      });
      setItem(next);
      setNotice("Ставки и предпочтения сохранены.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <PageShell
      eyebrow="Специалист"
      title="Ставки"
      description="Ставки, формат работы и тип занятости."
    >
      <div className="space-y-6">
        <IdPanel />
        <SpecialistRequiredId specialistId={specialistId.value} />
        {specialistId.value ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void save(event)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <SelectField
                label="Модель ставки"
                value={form.rateModel}
                onChange={(value) =>
                  setForm((current) => ({ ...current, rateModel: value as SpecialistRateModel }))
                }
                options={["hourly", "monthly", "hourly_or_monthly", "negotiable"]}
              />
              <TextInput
                label="Валюта"
                onChange={(value) =>
                  setForm((current) => ({ ...current, currencyCode: value.toUpperCase() }))
                }
                value={form.currencyCode}
              />
              <TextInput
                label="Hourly min"
                onChange={(value) => setForm((current) => ({ ...current, hourlyRateMin: value }))}
                type="number"
                value={form.hourlyRateMin}
              />
              <TextInput
                label="Hourly max"
                onChange={(value) => setForm((current) => ({ ...current, hourlyRateMax: value }))}
                type="number"
                value={form.hourlyRateMax}
              />
              <TextInput
                label="Monthly min"
                onChange={(value) => setForm((current) => ({ ...current, monthlyRateMin: value }))}
                type="number"
                value={form.monthlyRateMin}
              />
              <TextInput
                label="Monthly max"
                onChange={(value) => setForm((current) => ({ ...current, monthlyRateMax: value }))}
                type="number"
                value={form.monthlyRateMax}
              />
              <SelectField
                label="Формат"
                value={form.workFormat}
                onChange={(value) =>
                  setForm((current) => ({ ...current, workFormat: value as SpecialistWorkFormat }))
                }
                options={["remote", "hybrid", "onsite", "flexible"]}
              />
              <SelectField
                label="Занятость"
                value={form.employmentType}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    employmentType: value as SpecialistEmploymentType,
                  }))
                }
                options={["full_time", "part_time", "contract", "freelance", "project_based"]}
              />
              <SelectField
                label="Нагрузка"
                value={form.desiredWorkload}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    desiredWorkload: value as SpecialistDesiredWorkload,
                  }))
                }
                options={["full_time", "part_time", "flexible"]}
              />
              <div className="md:col-span-2">
                <TextArea
                  label="Заметки"
                  onChange={(value) => setForm((current) => ({ ...current, notes: value }))}
                  value={form.notes}
                />
              </div>
            </div>
            <button
              className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
              type="submit"
            >
              Сохранить ставки
            </button>
          </form>
        ) : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        {notice ? <InfoPanel title="Готово" text={notice} tone="success" /> : null}
        {item ? (
          <InfoPanel
            title="Текущие настройки"
            text={`${item.rateModel} · ${item.currencyCode} · ${item.workFormat} · ${item.employmentType}`}
          />
        ) : null}
      </div>
    </PageShell>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="text-sm text-slate-400">{label}</span>
      <select
        className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SpecialistAvailabilityPage() {
  const specialistId = useSpecialistId();
  const [item, setItem] = useState<SpecialistAvailability | null>(null);
  const [form, setForm] = useState({
    availabilityStatus: "available" as SpecialistAvailabilityStatus,
    availableFrom: "",
    weeklyCapacityHours: "",
    currentAllocationPercent: "0",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    if (!specialistId.value) return;
    try {
      const response = await getSpecialistAvailability(specialistId.value);
      setItem(response.item);
      if (response.item) {
        setForm({
          availabilityStatus: response.item.availabilityStatus,
          availableFrom: response.item.availableFrom ?? "",
          weeklyCapacityHours:
            response.item.weeklyCapacityHours === null
              ? ""
              : String(response.item.weeklyCapacityHours),
          currentAllocationPercent: String(response.item.currentAllocationPercent),
          notes: response.item.notes ?? "",
        });
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  useEffect(() => {
    void load();
  }, [specialistId.value]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    try {
      const next = await putSpecialistAvailability(specialistId.value, {
        availabilityStatus: form.availabilityStatus,
        availableFrom: nullable(form.availableFrom),
        weeklyCapacityHours: numberOrNull(form.weeklyCapacityHours),
        currentAllocationPercent: Number(form.currentAllocationPercent),
        lastConfirmedAt: new Date().toISOString(),
        notes: nullable(form.notes),
      });
      setItem(next);
      setNotice("Доступность сохранена.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <PageShell
      eyebrow="Специалист"
      title="Доступность"
      description="Статус доступности, capacity и текущая загрузка."
    >
      <div className="space-y-6">
        <IdPanel />
        <SpecialistRequiredId specialistId={specialistId.value} />
        {specialistId.value ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void save(event)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <SelectField
                label="Статус"
                value={form.availabilityStatus}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    availabilityStatus: value as SpecialistAvailabilityStatus,
                  }))
                }
                options={["available", "available_from", "unavailable", "not_looking"]}
              />
              <TextInput
                label="Available from YYYY-MM-DD"
                onChange={(value) => setForm((current) => ({ ...current, availableFrom: value }))}
                value={form.availableFrom}
              />
              <TextInput
                label="Weekly capacity hours"
                onChange={(value) =>
                  setForm((current) => ({ ...current, weeklyCapacityHours: value }))
                }
                type="number"
                value={form.weeklyCapacityHours}
              />
              <TextInput
                label="Allocation %"
                onChange={(value) =>
                  setForm((current) => ({ ...current, currentAllocationPercent: value }))
                }
                type="number"
                value={form.currentAllocationPercent}
              />
              <div className="md:col-span-2">
                <TextArea
                  label="Notes"
                  onChange={(value) => setForm((current) => ({ ...current, notes: value }))}
                  value={form.notes}
                />
              </div>
            </div>
            <button
              className="mt-5 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950"
              type="submit"
            >
              Сохранить доступность
            </button>
          </form>
        ) : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        {notice ? <InfoPanel title="Готово" text={notice} tone="success" /> : null}
        {item ? (
          <InfoPanel
            title="Текущая доступность"
            text={`${item.availabilityStatus} · ${item.weeklyCapacityHours ?? "—"} ч/нед · ${item.currentAllocationPercent}%`}
          />
        ) : null}
      </div>
    </PageShell>
  );
}
